import { useMemo, useState } from 'react';
import PincoTemplate from '../templates/PincoTemplate';
import LionTemplate from '../templates/LionTemplate';
import LionOnePageTemplate from '../templates/LionOnePageTemplate';
import PayPageGateV1 from '../templates/PayPageGateV1';
import DefaultTemplate from '../templates/DefaultTemplate';
import QuasiTemplate from '../templates/QuasiTemplate';
import { TemplateProps } from '../templates/types';
import { Transaction } from '../services/types';

type TplKey = 'pinco' | 'lion' | 'lion-onepage' | 'paypage-gate' | 'default' | 'quasi';
type PreviewMerchant = 'pinco' | 'idp' | 'vovan';
type ScreenKey =
  | 'sbp'
  | 'card'
  | 'no-receipt'
  | 'amount-changed'
  | 'upload'
  | 'receipt-check'
  | 'receipt-check-fail'
  | 'receipt-unconfirmed'
  | 'payment-expired'
  | 'receipt-check-stalled'
  | 'paid-leave'
  | 'low-time'
  | 'processing'
  | 'processing-dispute'
  | 'success'
  | 'slot'
  | 'variants'
  | 'variants-expired'
  | 'retry'
  | 'declined'
  | 'expired';

const ALL_SCREENS: ScreenKey[] = [
  'sbp',
  'card',
  'no-receipt',
  'amount-changed',
  'upload',
  'receipt-check',
  'receipt-check-fail',
  'receipt-unconfirmed',
  'payment-expired',
  'receipt-check-stalled',
  'paid-leave',
  'low-time',
  'processing',
  'processing-dispute',
  'success',
  'slot',
  'variants',
  'variants-expired',
  'retry',
  'declined',
  'expired',
];

// Экраны, которых в потоке шаблона нет: кнопка в панели показывала бы не тот экран, что подписан.
//  - `upload` — отдельный экран чека есть только у двухэкранных шаблонов;
//  - `low-time` — стандартная страница попап продления не рендерит;
//  - `processing-dispute` — о диспуте клиенту говорит только стандартная страница.
const SCREENS_OUTSIDE_FLOW: Record<TplKey, ScreenKey[]> = {
  pinco: ['processing-dispute', 'variants-expired'],
  lion: ['processing-dispute', 'variants-expired'],
  'lion-onepage': ['upload', 'processing-dispute', 'variants-expired'],
  'paypage-gate': ['upload', 'low-time', 'variants-expired'],
  default: ['processing-dispute', 'variants-expired'],
  // Клиент платит на странице партнёра: реквизита, чека и его проверки на этом шаблоне нет.
  quasi: [
    'payment-expired',
    'receipt-check-stalled',
    'processing',
    'sbp',
    'card',
    'no-receipt',
    'amount-changed',
    'upload',
    'receipt-check',
    'receipt-check-fail',
    'receipt-unconfirmed',
    'paid-leave',
    'low-time',
    'processing-dispute',
  ],
};

export const screensOf = (tpl: TplKey): ScreenKey[] =>
  ALL_SCREENS.filter(k => !SCREENS_OUTSIDE_FLOW[tpl].includes(k));

const MOCK_UUID = '5a2708b28c9943888cfe419a52e2366b';

// Stable across renders so the variant countdown doesn't reset every tick.
const PREVIEW_VARIANTS = Array.from({ length: 4 }, (_, i) => ({
  id: i + 1,
  amount: 19000 + i * 250,
}));
const PREVIEW_EXPIRE_AT = new Date(Date.now() + 3 * 60 * 1000).toISOString();
// Срок предложения плиток на проде (`VARIANT_TTL_LOW_SEC`).
const QUASI_OFFER_TTL_MS = 120 * 1000;

const baseTx: Transaction = {
  amount: 20002,
  oldAmount: null,
  externalUserToken: 'demo',
  isWasUnlocked: false,
  merchant: 'pinco',
  paymentMethod: 'SBER SBP RUB',
  paymentTicketHistory: [],
  redirectUrl: 'https://example.com',
  requisite: {
    currency: 'RUB',
    date: new Date().toISOString(),
    network: '',
    qrCodeData: '',
    type: 'SBP P2P',
    phone: '+79255093712',
    acquiringBank: 'sber',
    country: null,
    userManual: null,
    paymentUrl: null,
  },
  status: 'PAYMENT_PENDING',
  amountChanged: false,
  recreationPaymentMethods: [],
  simpleConfirmation: false,
};

const mockByScreen = (screen: ScreenKey): Transaction => {
  switch (screen) {
    case 'card':
      return {
        ...baseTx,
        paymentMethod: 'MC/VISA/MIR P2P RUB',
        requisite: {
          ...baseTx.requisite,
          type: 'MC P2P',
          phone: undefined as any,
          cardNumber: '2200150399881234',
        },
      };
    // CPAYMENT-1742: тикет, которому чек не нужен — блок загрузки и нумерация шага не рисуются.
    case 'no-receipt':
      return { ...baseTx, simpleConfirmation: true };
    // The system adjusted the amount: the payment screen opens under the amount-change notice.
    case 'amount-changed':
      return {
        ...baseTx,
        amount: 12000,
        oldAmount: 11500,
        amountChanged: true,
        amountChangeSource: 'customization',
      };
    // CPAYMENT-1616: авто-проверка чека — ожидание и отказ (можно перезалить).
    case 'receipt-check':
      return {
        ...baseTx,
        status: 'FILES_AUTO_VERIFICATION',
        receiptVerification: { phase: 'pending', canReupload: false },
      };
    case 'receipt-check-fail':
      return {
        ...baseTx,
        status: 'FILES_AUTO_VERIFICATION',
        receiptVerification: { phase: 'failed', canReupload: true },
      };
    // CPAYMENT-1616: чек так и не приняли — экран с призывом в поддержку вместо галочки.
    case 'receipt-unconfirmed':
      return {
        ...baseTx,
        status: 'FILES_VERIFICATION',
        receiptVerification: { phase: 'unconfirmed', canReupload: false },
      };
    // CPAYMENT-1716: ожидание кончилось ничем — таймаут тикета после «далее» и залипший гейт.
    case 'payment-expired':
      return { ...baseTx, status: 'EXPIRED', receiptIntent: true };
    case 'receipt-check-stalled':
      return {
        ...baseTx,
        status: 'FILES_AUTO_VERIFICATION',
        receiptVerification: {
          phase: 'pending',
          canReupload: false,
          expiresAt: new Date(Date.now() - 60_000).toISOString(),
        },
      };
    case 'processing':
      return { ...baseTx, status: 'FILES_VERIFICATION' };
    case 'processing-dispute':
      return { ...baseTx, status: 'FILES_VERIFICATION', disputeOpen: true };
    case 'success':
      return { ...baseTx, status: 'COMPLETED' };
    case 'slot':
      return { ...baseTx, status: 'WAITING_FOR_REQUISITE' };
    case 'variants':
      return {
        ...baseTx,
        amount: 23500,
        status: 'WAITING_FOR_VARIANT_SELECTION',
        currency: 'RUB',
        variants: PREVIEW_VARIANTS,
        variantsExpireAt: PREVIEW_EXPIRE_AT,
      } as Transaction;
    case 'variants-expired':
      return {
        ...baseTx,
        amount: 23500,
        status: 'WAITING_FOR_VARIANT_SELECTION',
        currency: 'RUB',
        variants: PREVIEW_VARIANTS,
        variantsExpireAt: new Date(0).toISOString(),
      } as Transaction;
    case 'retry':
      return {
        ...baseTx,
        status: 'CLIENT_PENDING',
        requisite: { ...baseTx.requisite, type: '' },
        paymentAdapterSearchAttemptsCounter: 1,
      };
    case 'declined':
      return { ...baseTx, status: 'DECLINED' };
    case 'expired':
      return { ...baseTx, status: 'EXPIRED' };
    case 'upload':
    case 'sbp':
    default:
      return baseTx;
  }
};

const formatTime = (s: number) => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

const noop = async () => {};
const noopSync = () => {};

const TOOLBAR_COLLAPSED_KEY = 'preview.toolbarCollapsed';

// Хранилище может быть недоступно (приватный режим, запрет сайта) — превью от этого ломаться не должно.
const readToolbarCollapsed = (): boolean => {
  try {
    return window.localStorage.getItem(TOOLBAR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
};

const writeToolbarCollapsed = (collapsed: boolean) => {
  try {
    window.localStorage.setItem(TOOLBAR_COLLAPSED_KEY, collapsed ? '1' : '0');
  } catch {
    // preview only — nothing to recover
  }
};

const Toolbar = ({
  tpl,
  screen,
  merchant,
  onTpl,
  onScreen,
  onMerchant,
}: {
  tpl: TplKey;
  screen: ScreenKey;
  merchant: PreviewMerchant;
  onTpl: (k: TplKey) => void;
  onScreen: (k: ScreenKey) => void;
  onMerchant: (k: PreviewMerchant) => void;
}) => {
  // Панель перекрывает верх экрана — ровно ту часть, которую чаще всего и надо разглядеть. Её можно
  // свернуть в полупрозрачную кнопку в углу; выбор запоминается, чтобы переживать перезагрузку.
  const [collapsed, setCollapsed] = useState(readToolbarCollapsed);
  const toggle = (next: boolean) => {
    setCollapsed(next);
    writeToolbarCollapsed(next);
  };

  if (collapsed) {
    return (
      <button
        type='button'
        title='Показать панель превью'
        aria-label='Показать панель превью'
        onClick={() => toggle(false)}
        style={{
          position: 'fixed',
          top: 8,
          left: 8,
          zIndex: 10000,
          width: 34,
          height: 34,
          borderRadius: '50%',
          border: 'none',
          background: 'rgba(20,20,20,0.9)',
          color: '#fff',
          fontSize: 16,
          lineHeight: 1,
          cursor: 'pointer',
          opacity: 0.35,
          transition: 'opacity 0.2s',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        }}
        onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; }}
        onMouseLeave={e => { e.currentTarget.style.opacity = '0.35'; }}
      >
        ☰
      </button>
    );
  }

  const box: React.CSSProperties = {
    position: 'fixed',
    top: 8,
    left: 8,
    zIndex: 10000,
    padding: 10,
    borderRadius: 10,
    background: 'rgba(20,20,20,0.9)',
    color: '#fff',
    fontFamily: 'system-ui, sans-serif',
    fontSize: 12,
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    flexWrap: 'wrap',
    maxWidth: 'calc(100vw - 16px)',
    boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
  };
  const btn = (active: boolean): React.CSSProperties => ({
    padding: '4px 10px',
    border: '1px solid',
    borderColor: active ? '#FF0123' : '#444',
    borderRadius: 6,
    background: active ? '#FF0123' : 'transparent',
    color: '#fff',
    cursor: 'pointer',
    fontSize: 12,
  });
  return (
    <div style={box}>
      <button
        type='button'
        title='Скрыть панель превью'
        aria-label='Скрыть панель превью'
        onClick={() => toggle(true)}
        style={{ ...btn(false), padding: '4px 8px', lineHeight: 1 }}
      >
        ×
      </button>
      <span style={{ opacity: 0.7 }}>Template:</span>
      {(['pinco', 'lion', 'lion-onepage', 'paypage-gate', 'default', 'quasi'] as TplKey[]).map(k => (
        <button key={k} style={btn(tpl === k)} onClick={() => onTpl(k)}>
          {k}
        </button>
      ))}
      <span style={{ opacity: 0.7, marginLeft: 8 }}>Screen:</span>
      {screensOf(tpl).map(k => (
        <button key={k} style={btn(screen === k)} onClick={() => onScreen(k)}>
          {k}
        </button>
      ))}
      {tpl === 'default' && (
        <>
          <span style={{ opacity: 0.7, marginLeft: 8 }}>Merchant:</span>
          {(['pinco', 'idp', 'vovan'] as PreviewMerchant[]).map(k => (
            <button key={k} style={btn(merchant === k)} onClick={() => onMerchant(k)}>
              {k}
            </button>
          ))}
        </>
      )}
    </div>
  );
};

const TemplatePreview = () => {
  const [tpl, setTpl] = useState<TplKey>('pinco');
  const [screen, setScreen] = useState<ScreenKey>('sbp');
  const [previewMerchant, setPreviewMerchant] = useState<PreviewMerchant>('pinco');

  // Экрана может не быть в потоке нового шаблона — тогда открываем его первый экран.
  const selectTemplate = (next: TplKey) => {
    setTpl(next);
    if (!screensOf(next).includes(screen)) setScreen(next === 'quasi' ? 'variants' : 'sbp');
  };

  // Таймер идёт с момента открытия экрана: каждый заход — новое предложение на полный срок.
  const quasiOfferExpireAt = useMemo(
    () => new Date(Date.now() + QUASI_OFFER_TTL_MS).toISOString(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tpl, screen],
  );

  const transaction = {
    ...mockByScreen(screen),
    merchant: tpl === 'default' ? previewMerchant : mockByScreen(screen).merchant,
    ...(tpl === 'quasi' ? { paymentMethod: 'P2P QUASI ECOM RUB' } : {}),
    ...(tpl === 'quasi' && screen === 'variants' ? { variantsExpireAt: quasiOfferExpireAt } : {}),
  };

  const props: TemplateProps = {
    transaction,
    loading: false,
    error: null,
    timeLeft: screen === 'low-time' ? 118 : 597,
    timeoutScreen:
      screen === 'payment-expired'
        ? 'payment-expired'
        : screen === 'receipt-check-stalled'
          ? 'gate-stalled'
          : null,
    uuid: MOCK_UUID,
    showConfirm: screen === 'upload',
    transgranError: false,
    // CPAYMENT-1730: экран реквизита глазами клиента, уже нажавшего «Я оплатил» — «Назад»
    // открывает выбор «уйти к мерчанту / отменить заявку», а не молчаливый деклайн.
    receiptIntent: screen === 'paid-leave',
    // CPAYMENT-1731: попап «мало времени» — в превью его открывает сам экран, без ожидания
    // таймера; условие показа на живой странице считает `services/lowTimeWarning`.
    lowTimeWarning: screen === 'low-time',
    onExtendTime: noopSync,
    onDismissLowTime: noopSync,
    onConfirm: noop,
    onConfirmWithReceipt: async () => ({ ok: true, alreadyUploaded: false, attempts: 1 }),
    onCancel: noop,
    onDeclineOffer: async (context) => window.alert(`Отказ: ${context}`),
    onLeave: noopSync,
    onRedirect: noopSync,
    onSelectVariant: (id: number) => window.alert(`Выбран вариант #${id}`),
    variantSubmitting: false,
    setRetry: noopSync,
    setShowConfirm: noopSync,
    setTransgranError: noopSync,
    setLoading: noopSync,
    formatTime,
  };

  const Template =
    tpl === 'pinco'
      ? PincoTemplate
      // Двухэкранный LionTemplate клиентам больше не выдаётся, но остаётся в репозитории —
      // превью единственное место, откуда на него можно посмотреть.
      : tpl === 'lion'
        ? LionTemplate
        // Стандартная страница: реквизиты и чек на одном экране.
        : tpl === 'lion-onepage'
          ? LionOnePageTemplate
          // Второй одностраничный экран: тот же поток, переписанные тексты и состояния.
          : tpl === 'paypage-gate'
            ? PayPageGateV1
            : tpl === 'quasi'
              ? QuasiTemplate
              : DefaultTemplate;

  return (
    <>
      <Toolbar
        tpl={tpl}
        screen={screen}
        merchant={previewMerchant}
        onTpl={selectTemplate}
        onScreen={setScreen}
        onMerchant={setPreviewMerchant}
      />
      {/* Каждый экран превью — отдельная заявка: шаблон помнит, что было с заявкой раньше (например,
          что она уже была на проверке), и без пересоздания перенёс бы это на следующий экран. */}
      <Template key={`${tpl}:${screen}`} {...props} />
    </>
  );
};

export default TemplatePreview;
